/**
 * Operations → Placed book.
 *
 * The census of the Placed book — the ~180 cases a Handling Firm has taken
 * and is working toward settlement. This is the thickest, slowest-moving
 * part of the pipeline, and by design the one that needs the LEAST active
 * attention, so the surface is a monitoring census, not a worklist: there is
 * nothing to *do* per row. Cases are bucketed into four age bands by time-in-
 * Placed; you read the shape off the tab counts, then click a band to look
 * through it.
 *
 * LAYOUT (portal-imprint, D1=hybrid): a portal-style <Table> under the band
 * tabs. The bands stay (they're the glance); within a band the cases are a
 * table, and a row opens the shared CaseDetailPanel (replacing the old inline
 * expand — the placed-date / OF detail now lives in the panel).
 *
 * Age honesty (ADR-0045): ~38% of the book was brought in by the 2026-04-27
 * migration, which stamped the migration date as their Placed Date. The
 * composer repairs those ages off the lead's real Creation Date and flags
 * them; here they carry a quiet "est." tag so a case near a band boundary is
 * never read as exact.
 *
 * `PlacedRow` (below) is unchanged — The Surface renders placed matters as
 * that expandable card in its mixed-phase overlay.
 *
 * Read-only. Cases enter (FSA returns) and leave (settle/close) upstream;
 * the surface re-derives on each poll.
 *
 * Spec: engineering/command-center/docs/port-notes/holding-pen.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import {
  PLACED_BANDS,
  type PlacedBand,
  type PlacedCase,
} from "@command-center/shared";
import { cn } from "@/lib/utils";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { placedToDetail, type CaseDetail } from "@/lib/case-detail";
import { fmtDate, fmtDuration } from "@/lib/format";
import { usePlaced } from "@/lib/use-placed";

/** Friendly tab labels for the band keys. */
const BAND_LABELS: Record<PlacedBand, string> = {
  "0-30d": "0–30 days",
  "30d-6mo": "30 days – 6 months",
  "6mo-1yr": "6 months – 1 year",
  "1yr+": "1 year+",
};

/** data-testid-safe slug for a band key. */
function bandSlug(band: PlacedBand): string {
  return band.replace(/[^a-z0-9]/gi, "");
}

export function PlacedPage() {
  const placedBook = usePlaced();
  // The fresh band is the one with loose ends to watch — the default view.
  const [band, setBand] = useState<PlacedBand>("0-30d");
  // The case-detail side panel is lifted to the page so it stays open while
  // you read down the list.
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  // Client-name filter (portal parity). Band counts stay totals.
  const [query, setQuery] = useState("");

  if (placedBook.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (placedBook.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="placed-error">
        Couldn't load the Placed book. It will retry automatically.
      </div>
    );
  }

  const cases = placedBook.data?.cases ?? [];
  const counts = placedBook.data?.counts ?? [];
  const countFor = (b: PlacedBand) =>
    counts.find((c) => c.band === b)?.count ?? 0;
  const searching = query.trim() !== "";
  const inBand = cases
    .filter((c) => c.band === band)
    .filter((c) => matchesQuery(c.client, query));

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="placed">
      <BookHeading phaseKey="placed">
        <span
          className="text-sm text-plane-muted"
          data-testid="placed-count"
        >
          {cases.length === 0
            ? ""
            : `${cases.length} placed case${cases.length === 1 ? "" : "s"}`}
        </span>
      </BookHeading>

      {/* Band tabs — the glance: how the book is distributed by age. */}
      <div
        className="mb-4 flex flex-wrap gap-1 border-b"
        role="tablist"
        data-testid="placed-tabs"
      >
        {PLACED_BANDS.map((b) => (
          <button
            key={b}
            type="button"
            role="tab"
            aria-selected={b === band}
            onClick={() => setBand(b)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              b === band
                ? "border-active-edge font-medium text-plane-foreground"
                : "border-transparent text-plane-muted hover:text-plane-foreground",
            )}
            data-testid={`placed-tab-${bandSlug(b)}`}
          >
            {BAND_LABELS[b]}
            <span className="ml-2 tabular-nums text-plane-muted">
              {countFor(b)}
            </span>
          </button>
        ))}
      </div>

      <SearchBar value={query} onChange={setQuery} testId="placed-search" />

      {inBand.length === 0 ? (
        <div
          className="flex min-h-[40vh] items-center justify-center text-center"
          data-testid="placed-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "No cases in this band."}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Handling firm</TableHead>
                <TableHead>Case type</TableHead>
                <TableHead>Time in placed</TableHead>
                <TableHead>
                  <span className="sr-only">Watch</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inBand.map((c) => (
                <PlacedTableRow
                  key={c.id}
                  holdingCase={c}
                  onOpenSummary={() => setDetail(placedToDetail(c))}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="placed-summary-panel"
        />
      ) : null}
    </section>
  );
}

/** One placed matter as a portal-style table row. The age (with the "est." tag
 *  for migration-anchored cases) is the census axis; the row opens the detail
 *  panel for the placed-date / OF substrate. */
function PlacedTableRow({
  holdingCase: c,
  onOpenSummary,
}: {
  holdingCase: PlacedCase;
  onOpenSummary: () => void;
}) {
  return (
    <TableRow onClick={onOpenSummary} data-testid="placed-row">
      <TableCell className="font-medium">{c.client}</TableCell>
      <TableCell className={c.handlingFirmName ? undefined : "text-muted-foreground"}>
        {c.handlingFirmName ?? "Unknown firm"}
      </TableCell>
      <TableCell className={c.caseType ? "text-muted-foreground" : "text-muted-foreground"}>
        {c.caseType ?? "—"}
      </TableCell>
      <TableCell className="tabular-nums" data-testid="placed-age">
        {fmtDuration(c.daysInPlaced)}
        {c.ageEstimated ? (
          <span
            className="ml-1 text-[11px] font-normal text-warning"
            title="Estimated — this case was migrated; its age is anchored on the lead's creation date, not its placed date."
            data-testid="placed-est"
          >
            est.
          </span>
        ) : null}
      </TableCell>
      <TableCell className="w-0 text-right" onClick={(e) => e.stopPropagation()}>
        <SurfaceTrigger leadId={c.leadRecordId} />
      </TableCell>
    </TableRow>
  );
}

export function PlacedRow({
  holdingCase,
  onOpenSummary,
}: {
  holdingCase: PlacedCase;
  onOpenSummary: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      className="relative rounded-lg border bg-card text-card-foreground"
      data-testid="placed-row"
    >
      <SurfaceTrigger
        leadId={holdingCase.leadRecordId}
        className="absolute right-2 top-2 z-10"
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 pr-20 text-left"
        aria-expanded={expanded}
        data-testid="placed-row-toggle"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        {/* Who, and with which firm. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium">{holdingCase.client}</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <ArrowRight className="h-3 w-3 shrink-0 opacity-60" />
            {holdingCase.handlingFirmName ?? "Unknown firm"}
          </span>
          {holdingCase.caseType ? (
            <span className="text-xs text-muted-foreground">
              · {holdingCase.caseType}
            </span>
          ) : null}
        </div>

        {/* How long in Placed — the age axis. The "est." tag marks a
            migration-anchored estimate (ADR-0045). */}
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums"
            data-testid="placed-age"
          >
            {fmtDuration(holdingCase.daysInPlaced)}
          </span>
          {holdingCase.ageEstimated ? (
            <span
              className="ml-1 text-[11px] font-normal text-warning"
              title="Estimated — this case was migrated; its age is anchored on the lead's creation date, not its placed date."
              data-testid="placed-est"
            >
              est.
            </span>
          ) : null}
          <span className="block text-[11px] text-muted-foreground">
            in placed
          </span>
        </span>
      </button>

      {expanded ? (
        <div
          className="border-t px-4 py-3 pl-10 text-xs"
          data-testid="placed-detail"
        >
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {holdingCase.placedDate
                ? holdingCase.ageEstimated
                  ? `Placed date unknown (migrated) · est. from intake`
                  : `Placed ${fmtDate(holdingCase.placedDate, { year: true })}`
                : "No placed date on file"}
              {holdingCase.originatingFirmName
                ? ` · from ${holdingCase.originatingFirmName}`
                : ""}
            </span>
            <button
              type="button"
              onClick={onOpenSummary}
              className="font-medium text-interactive hover:underline"
              data-testid="placed-open-summary"
            >
              Case summary →
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}


